import {
  Button,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { FiPlus } from "react-icons/fi";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import isEmpty from "lodash/isEmpty";
import { useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { CiEdit } from "react-icons/ci";
import { FaRegFilePdf } from "react-icons/fa6";
import { LuPrinter } from "react-icons/lu";
import { MdDeleteOutline } from "react-icons/md";
import { RiFileExcel2Line } from "react-icons/ri";
import { useLocation, useOutletContext } from "react-router";
import { useReactToPrint } from "react-to-print";
import * as XLSX from "xlsx";
import { IMAGES } from "../../assets/images";
import FloatingButtonContainer from "../../components/floatingButton";
import FullScreenLoader from "../../components/fullScreenLoader";
import Header from "../../components/header";
import PillInfo from "../../components/pillInfo";
import { CONSTANTS } from "../../constants";
import { fetcher } from "../../network";
import { API } from "../../network/api";
import { fetchPatientById } from "../../network/globalFetches";
import { Patient, RoleType, TimeToTakePills } from "../../types";
import { useTypedNavigate } from "../../types/navigation";
import { RemovePillResponse } from "../../types/responses";
import { PARTS_OF_THE_DAY, reverseHebrew } from "../../utils";
import DosagePrintTable from "./dosagePrintTable";

const removePillMutation = async ({
  patientId,
  pillId,
}: {
  patientId: string;
  pillId: string;
}) => {
  const { data } = await fetcher().delete<RemovePillResponse>(
    API.DELETE.REMOVE_PILL(patientId, pillId),
  );
  return data;
};

const updatePillMutation = async ({
  patientId,
  pillId,
  dosage,
}: {
  patientId: string;
  pillId: string;
  dosage: {
    value: string;
    partOfTheDay: string;
  };
}) => {
  const { data } = await fetcher().put<{ patient: Patient }>(
    API.PUT.EDIT_PILL(patientId, pillId),
    {
      dosage,
    },
  );
  return data.patient;
};

const exportDosageTableToPDF = async (patient: Patient) => {
  const currentDate = dayjs().format("DD/MM/YYYY");
  const fontUrl = "/fonts/NotoSansHebrew-Regular.ttf";

  const fontBytes = await fetch(fontUrl).then((res) => res.arrayBuffer());
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "a4",
  });

  // הטמעת הפונט
  const fontName = "NotoSansHebrew";
  doc.addFileToVFS(
    "NotoSansHebrew.ttf",
    btoa(String.fromCharCode(...new Uint8Array(fontBytes))),
  );
  doc.addFont("NotoSansHebrew.ttf", fontName, "normal");
  doc.setFont(fontName);
  doc.setFontSize(14);

  // כותרת
  doc.text(reverseHebrew(`שם הדייר: ${patient.name}`), 540, 40, {
    align: "right",
  });
  doc.text(reverseHebrew(`תאריך: ${currentDate}`), 540, 60, { align: "right" });

  const dosageData = patient.pills.map((pill) => {
    //@ts-ignore
    const morning = pill.dosages.find((d) => d.morning)?.morning?.value || "-";
    //@ts-ignore
    const noon = pill.dosages.find((d) => d.noon)?.noon?.value || "-";
    //@ts-ignore
    const evening = pill.dosages.find((d) => d.evening)?.evening?.value || "-";
    //@ts-ignore
    const night = pill.dosages.find((d) => d.night)?.night?.value || "-";

    return [
      pill.isSOS ? "ןכ" : "",
      reverseHebrew(night),
      reverseHebrew(evening),
      reverseHebrew(noon),
      reverseHebrew(morning),
      reverseHebrew(pill.dragHebName),
    ];
  });

  autoTable(doc, {
    startY: 80,
    head: [["רודככ רדגוה S.O.S ", "הליל", "ברע", "םיירהצ", "רקוב", "רודכה םש"]],
    body: dosageData,
    styles: {
      font: fontName,
      fontStyle: "normal",
      halign: "right",
    },
    headStyles: { halign: "right", fillColor: [220, 220, 220] },
    bodyStyles: { halign: "right" },
  });

  doc.save(`מינון כדורים של ${patient.name} - תאריך ${currentDate}.pdf`);
};

const exportDosageTableToExcel = (patient: Patient) => {
  const workbook = XLSX.utils.book_new();

  const dosageData = patient.pills.map((pill) => {
    //@ts-ignore
    const morning = pill.dosages.find((d) => d.morning)?.morning?.value || 0;
    //@ts-ignore
    const noon = pill.dosages.find((d) => d.noon)?.noon?.value || 0;
    //@ts-ignore
    const evening = pill.dosages.find((d) => d.evening)?.evening?.value || 0;
    //@ts-ignore
    const night = pill.dosages.find((d) => d.night)?.night?.value || 0;

    return {
      "שם הכדור": pill.dragHebName,
      בוקר: morning || "-",
      צהריים: noon || "-",
      ערב: evening || "-",
      לילה: night || "-",
      SOS: pill.isSOS ? "כן" : "לא",
    };
  });

  const currentDate = dayjs().format("DD/MM/YYYY");

  // Headers
  const titleRow = [`שם הדייר: ${patient.name}`, `תאריך: ${currentDate}`];
  const emptyRow: string[] = [];

  // Convert JSON to array of arrays (for precise control)
  const headers = Object.keys(dosageData[0]);
  const dataRows = dosageData.map((row) => Object.values(row));

  const aoa = [titleRow, emptyRow, headers, ...dataRows];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  XLSX.utils.book_append_sheet(workbook, worksheet, "מינונים לפי זמנים");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const data = new Blob([excelBuffer], { type: "application/octet-stream" });

  saveAs(data, `מינון כדורים של ${patient.name} - תאריך ${currentDate}.xlsx`);
};

const PillsManagerPage = () => {
  const patientId = useLocation().state.patientId as string;
  const { role } = useOutletContext<{ username: string; role: RoleType }>();

  const [editingPillId, setEditingPillId] = useState<string | null>(null);
  const [editedDosages, setEditedDosages] = useState<{ [key: string]: string }>(
    {},
  );
  const printRef = useRef<HTMLDivElement>(null);
  const reactToPrintFn = useReactToPrint({ contentRef: printRef });
  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();

  const [selectedTab, setSelectedTab] = useState<0 | 1>(0);

  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError,
  } = useQuery({
    queryKey: fetchPatientById.queryKey(patientId),
    queryFn: fetchPatientById.fetch(patientId),
  });

  const { mutate: removePill } = useMutation({
    mutationFn: removePillMutation,
    onSuccess: ({ updatedPatient }) => {
      queryClient.setQueryData(
        fetchPatientById.queryKey(patientId),
        updatedPatient,
      );
      toast.success("הכדור נמחק בהצלחה");
    },
    onError: () => {
      toast.error("שגיאה - לא הצלחנו למחוק את הכדור");
    },
  });

  const { mutate: updatePill } = useMutation({
    mutationFn: updatePillMutation,
    onSuccess: (patient) => {
      queryClient.setQueryData(fetchPatientById.queryKey(patientId), patient);
      toast.success("העדכון בוצע בהצלחה");
    },
    onError: () => {
      toast.error("שגיאה נוצרה במערכת, אנא נסה שנית מאוחר יותר");
    },
  });

  if (isLoadingPatient) return <FullScreenLoader />;
  if (isError || !patient) return <p>error...</p>;

  const handleDeletePill = (pillId: string) => {
    removePill({ patientId: patient?._id || "", pillId });
  };

  const handleEditPill = (pillId: string) => {
    setEditingPillId(pillId);
  };

  const handleDosageChange = (key: string, value: string) => {
    setEditedDosages((prev) => ({
      ...prev,
      [key]: value === "0" ? "" : value,
    }));
  };

  const handleSavePill = () => {
    const value = Object.values(editedDosages)[0]?.toString();
    if (isEmpty(value)) {
      toast.error("השדה חייב להיות מלא כדי לשמור");
      return;
    }

    updatePill({
      pillId: editingPillId || "",
      patientId: patient._id,
      dosage: {
        partOfTheDay: Object.keys(editedDosages)[0]?.toString() || "",
        value: value === "" ? "0" : value,
      },
    });
    setEditingPillId(null);
    setEditedDosages({});
  };

  const handleCancelEditing = () => {
    setEditingPillId(null);
    setEditedDosages({});
  };

  const renderTableByPartOfTheDay = () => (
    <TableContainer className="mt-8" component={Paper}>
      <Table>
        <TableHead
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}>
          <TableRow>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              הערות
            </TableCell>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              שם הכדור
            </TableCell>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              כדור S.O.S
            </TableCell>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              בוקר
            </TableCell>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              צהריים
            </TableCell>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              ערב
            </TableCell>
            <TableCell
              style={{ textAlign: "center", fontWeight: "bold", fontSize: 18 }}>
              לילה
            </TableCell>
            {role !== "guide" && (
              <TableCell
                style={{
                  textAlign: "center",
                  fontWeight: "bold",
                  fontSize: 18,
                }}>
                פעולות
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {patient.pills
            .slice()
            .sort((a, b) => Number(a.isSOS) - Number(b.isSOS))
            .map((pill) => (
              <TableRow
                className={`${
                  pill.isSOS ? "bg-red-200" : "odd:bg-slate-100"
                } border-b-2 border-gray-500`}
                key={pill._id}>
                <TableCell style={{ textAlign: "center" }}>
                  {!pill.notes ? (
                    <p>-</p>
                  ) : (
                    <PillInfo notes={pill.notes || "אין הערות"} />
                  )}
                </TableCell>
                <TableCell
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: 16,
                  }}>
                  {pill.dragHebName}
                </TableCell>

                <TableCell
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: 16,
                  }}>
                  {pill.isSOS && "כן"}
                </TableCell>

                {/* Dosages for each time of day */}
                {["morning", "noon", "evening", "night"].map((time) => {
                  const dosage = pill.dosages.find((dosage) =>
                    Object.keys(dosage).includes(time),
                  );

                  return (
                    <TableCell
                      key={time}
                      style={{ textAlign: "center", fontSize: 16 }}>
                      {editingPillId === pill._id ? (
                        <TextField
                          inputMode="numeric"
                          value={
                            //@ts-ignore
                            editedDosages[time] ?? (dosage?.[time]?.value || "")
                          }
                          onChange={(e) =>
                            handleDosageChange(time, e.target.value)
                          }
                          size="small"
                        />
                      ) : dosage ? (
                        //@ts-ignore
                        `${dosage[time].value} מ״ג`
                      ) : (
                        "--"
                      )}
                    </TableCell>
                  );
                })}

                {/* Actions */}
                {role !== "guide" && (
                  <TableCell>
                    <div className="flex flex-col gap-4 justify-center">
                      {pill._id === editingPillId ? (
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={handleSavePill}>
                          שמור
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          startIcon={<CiEdit />}
                          onClick={() => handleEditPill(pill._id)}>
                          ערוך
                        </Button>
                      )}

                      {pill._id === editingPillId ? (
                        <Button
                          variant="contained"
                          color="warning"
                          size="small"
                          onClick={handleCancelEditing}>
                          ביטול עריכה
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          startIcon={<MdDeleteOutline />}
                          onClick={() => handleDeletePill(pill._id)}>
                          מחק
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTableByPill = () => (
    <TableContainer className="mt-8" component={Paper}>
      <Table>
        <TableHead
          sx={{
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}>
          <TableRow>
            <TableCell
              style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: 18,
              }}>
              שם הכדור
            </TableCell>
            <TableCell
              style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: 18,
              }}>
              מינון
            </TableCell>
            <TableCell
              style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: 18,
              }}>
              תאריך התחלה
            </TableCell>
            <TableCell
              style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: 18,
              }}>
              תאריך סיום
            </TableCell>
            <TableCell
              style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: 18,
              }}>
              פעולות
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {patient.pills
            .slice()
            .sort((a, b) => Number(a.isSOS) - Number(b.isSOS))
            .map((pill) => (
              <TableRow
                className={`${
                  pill.isSOS ? "bg-red-200" : "odd:bg-slate-100"
                } border-b-2 border-gray-500`}
                key={pill._id}>
                <TableCell
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: 16,
                  }}>
                  {pill.dragHebName} {pill.isSOS && " - S.O.S"}
                </TableCell>
                <TableCell className="min-w-40">
                  {pill.dosages.map((dosage, index) => {
                    // @ts-ignore
                    const [key, valueObj] = Object.entries(dosage).find(
                      ([key]) => key !== "_id",
                    )! as {
                      key: TimeToTakePills;
                      valueObj: { value: number };
                    };

                    return (
                      <div
                        key={pill._id + index + key + valueObj.value}
                        className="mb-2">
                        {editingPillId === pill._id ? (
                          <div className="flex gap-2 items-center">
                            <p>
                              {PARTS_OF_THE_DAY[
                                key as "morning" | "noon" | "evening" | "night"
                              ].labelHe || ""}
                            </p>
                            <TextField
                              inputMode="numeric"
                              value={editedDosages[key] ?? valueObj.value}
                              onChange={(e) => {
                                handleDosageChange(key, e.target.value);
                              }}
                              size="small"
                            />
                          </div>
                        ) : (
                          <p>
                            {PARTS_OF_THE_DAY[
                              key as "morning" | "noon" | "evening" | "night"
                            ].labelHe || ""}{" "}
                            - {valueObj.value} מ״ג
                          </p>
                        )}
                      </div>
                    );
                  })}
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  {dayjs(pill.dateRange.startDate).format("DD/MM/YYYY")}
                </TableCell>
                <TableCell style={{ textAlign: "center" }}>
                  {pill.dateRange.endDate
                    ? dayjs(pill.dateRange.endDate).format("DD/MM/YYYY")
                    : "--"}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-4 justify-center">
                    {/* Edit Action */}
                    {pill._id === editingPillId ? (
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={handleSavePill}>
                        שמור
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        startIcon={<CiEdit />}
                        onClick={() => handleEditPill(pill._id)}>
                        ערוך
                      </Button>
                    )}

                    {/* Delete Action */}
                    {pill._id === editingPillId ? (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={handleCancelEditing}>
                        ביטול עריכה
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        startIcon={<MdDeleteOutline />}
                        onClick={() => handleDeletePill(pill._id)}>
                        מחק
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className="w-4/5 mx-auto h-screen flex flex-col">
      <Header>
        <Header.Time />
        <Header.Title title="מסך ניהול תרופות" />
        <Header.Logo />
      </Header>

      <div className="text-center font-bold text-xl">
        <h2>שם הדייר: {patient.name}</h2>
      </div>

      <Tabs
        className="mt-4"
        value={selectedTab}
        onChange={(_, newValue) => setSelectedTab(newValue)}
        indicatorColor="primary"
        textColor="primary"
        centered>
        <Tab label="תצוגה לפי זמני היום" />
        <Tab label="תצוגה לפי כדור" />
      </Tabs>

      {isEmpty(patient.pills) ? (
        <div className="flex flex-col flex-1 mt-16 items-center gap-6">
          <h1 className="text-xl font-semibold">לא נמצאו תרופות</h1>
          <img src={IMAGES.PILLS_NOT_FOUND} className="w-1/2" />
        </div>
      ) : selectedTab === 0 ? (
        renderTableByPartOfTheDay()
      ) : (
        renderTableByPill()
      )}

      <FloatingButtonContainer side="right">
        <Tooltip
          title="לחץ להדפסה"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            bgColor="white"
            icon={<LuPrinter color="black" className="text-2xl" />}
            onClick={reactToPrintFn}
          />
        </Tooltip>
        <Tooltip
          title="יצא קובץ PDF"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            bgColor="white"
            icon={<FaRegFilePdf color="black" className="text-2xl" />}
            onClick={() => exportDosageTableToPDF(patient)}
          />
        </Tooltip>
        <Tooltip
          title="יצא קובץ Excel"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            bgColor="white"
            icon={<RiFileExcel2Line color="black" className="text-2xl" />}
            onClick={() => exportDosageTableToExcel(patient)}
          />
        </Tooltip>
        <Tooltip
          title="הוסף כדור"
          placement="right"
          className="hover:scale-105">
          <FloatingButtonContainer.Button
            bgColor="white"
            icon={<FiPlus color="black" size={20} />}
            onClick={() => {
              if (!patient) {
                return;
              }
              navigate(CONSTANTS.ROUTES.addMedication, {
                state: { patientId: patient._id, patientName: patient.name },
              });
            }}
          />
        </Tooltip>
      </FloatingButtonContainer>
      <Toaster position="bottom-center" />
      <div style={{ display: "none" }}>
        <DosagePrintTable ref={printRef} patient={patient} />
      </div>
    </div>
  );
};

export default PillsManagerPage;
